/**
 * Colour maths shared by every surface that dresses itself in an organiser's
 * brand: the staff app, the booking portal, and anything else that follows.
 *
 * This lived inside the staff theming module until the booking portal needed
 * the same contrast checks. It is pure arithmetic with no opinion about which
 * surface is asking.
 */

export const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function toRgb(hex: string): [number, number, number] | null {
  if (!HEX.test(hex)) return null;
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/** WCAG relative luminance. */
function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const ra = toRgb(a);
  const rb = toRgb(b);
  if (!ra || !rb) return 1;
  const la = luminance(ra);
  const lb = luminance(rb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export function isDark(hex: string): boolean {
  const rgb = toRgb(hex);
  if (!rgb) return true;
  return luminance(rgb) < 0.5;
}

/**
 * Pick a readable colour: keep the organiser's choice when it is legible on the
 * background, otherwise fall back to plain white or black.
 *
 * 3.5:1 is deliberately below the 4.5:1 body-text bar. Holding organisers to
 * full AA would override a lot of legitimate brand choices; this only catches
 * the ones that are genuinely unreadable.
 */
export function readableOn(background: string, preferred: string | undefined, minimum = 3.5): string {
  // Whichever of white or black actually reads better, rather than picking by
  // a lightness threshold. A mid-tone accent defeats a threshold: Tryka's
  // orange counts as "dark" on luminance, so a threshold hands it white at
  // 2.5:1 when black would have given 8.3:1.
  const fallback = contrastRatio(background, '#FFFFFF') >= contrastRatio(background, '#111111') ? '#FFFFFF' : '#111111';
  if (!preferred || !HEX.test(preferred)) return fallback;
  return contrastRatio(background, preferred) >= minimum ? preferred : fallback;
}

/**
 * Lay white or black over a colour at `alpha`, so cards and borders sit a step
 * off the background whichever direction the background runs. Returns an opaque
 * hex, because these land on CSS variables that other rules composite against.
 */
export function veil(hex: string, alpha: number): string {
  const rgb = toRgb(hex);
  if (!rgb) return hex;
  const over = isDark(hex) ? 255 : 0;
  const mixed = rgb.map((c) => Math.round(c + (over - c) * alpha));
  return `#${mixed.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** `rgba(r, g, b, a)` from a hex, for tints that must sit over whatever is behind them. */
export function rgba(hex: string, alpha: number): string | null {
  const rgb = toRgb(hex);
  if (!rgb) return null;
  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
}
